import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, ZapIcon, AwardIcon } from 'lucide-react';
export function Doctor() {
  const {
    t
  } = useLanguage();
  const pillars: {
    title: string;
    body: string;
  }[] = t('doctor.pillars');
  const badges: {
    value: string;
    label: string;
  }[] = t('doctor.badges');
  const easeOutCubic = [0.33, 1, 0.68, 1];
  const featureIcons = [ShieldCheckIcon, ZapIcon, AwardIcon];
  return <section id="doctor" className="py-20 lg:py-28 bg-white overflow-hidden" data-id="element-91">
      <div className="container mx-auto px-6" data-id="element-92">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-stretch max-w-6xl mx-auto" data-id="element-93">
          {/* Institutional visual column — dense, no portrait */}
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 1,
          ease: easeOutCubic
        }} className="relative" data-id="element-94">
            {/* Decorative offset frame */}
            <div className="absolute -top-5 -right-5 lg:-top-6 lg:-right-6 w-full h-full rounded-[2rem] border border-brand-gold/30 -z-10" data-id="element-95"></div>
            <div className="absolute -bottom-5 -left-5 lg:-bottom-6 lg:-left-6 w-28 h-28 bg-brand-blush/40 rounded-full blur-3xl -z-10" data-id="element-96"></div>

            <div className="rounded-[2rem] overflow-hidden relative z-10 bg-gradient-to-br from-brand-cream via-brand-stone/30 to-brand-blush/30 p-8 lg:p-10 h-full flex flex-col" data-id="element-97">
              {/* Eyebrow */}
              <div className="inline-flex items-center gap-3 mb-5" data-id="element-98">
                <span className="w-8 h-px bg-brand-gold" data-id="element-99"></span>
                <span className="text-brand-dark/50 font-medium tracking-[0.25em] text-[10px] uppercase" data-id="element-100">
                  Phoenix, AZ
                </span>
              </div>

              {/* Editorial headline */}
              <p className="font-serif text-[1.75rem] lg:text-[2.25rem] font-light text-brand-dark leading-[1.15] italic mb-8" data-id="element-101">
                Una sola especialización.
                <br data-id="element-102" />
                Hecha
                <span className="text-brand-gold not-italic" data-id="element-103">
                  {' '}
                  excepcionalmente bien.
                </span>
              </p>

              {/* Feature list — fills the dead space */}
              <ul className="space-y-4 mb-8 pb-8 border-b border-brand-dark/10 flex-1" data-id="element-104">
                {pillars.map((pillar, idx) => {
                const Icon = featureIcons[idx] || ShieldCheckIcon;
                return <li key={idx} className="flex items-start gap-3" data-id="element-105">
                      <Icon className="w-4 h-4 text-brand-gold shrink-0 mt-1" strokeWidth={1.5} data-id="element-106" />
                      <span className="text-sm text-brand-dark/75 font-light leading-snug" data-id="element-107">
                        {pillar.title}
                      </span>
                    </li>;
              })}
              </ul>

              {/* Institutional badges grid */}
              <div className="grid grid-cols-2 gap-x-4 gap-y-5" data-id="element-108">
                {badges.map((badge, idx) => <div key={idx} className="flex flex-col" data-id="element-109">
                    <span className="font-serif text-2xl lg:text-[1.75rem] text-brand-dark font-light leading-none mb-1.5" data-id="element-110">
                      {badge.value}
                    </span>
                    <span className="text-[10px] font-medium tracking-widest uppercase text-brand-dark/50 leading-tight" data-id="element-111">
                      {badge.label}
                    </span>
                  </div>)}
              </div>
            </div>

            {/* Floating institutional mini-card */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.9,
            delay: 0.5,
            ease: easeOutCubic
          }} className="absolute -bottom-5 right-4 lg:right-6 bg-brand-cream/95 backdrop-blur-md border border-brand-stone/40 rounded-2xl px-5 py-3 z-20 shadow-sm" data-id="element-112">
              <div className="flex items-center gap-3" data-id="element-113">
                <span className="w-6 h-px bg-brand-gold" data-id="element-114"></span>
                <p className="text-[10px] font-medium tracking-widest uppercase text-brand-dark/60" data-id="element-115">
                  FDA · Arizona
                </p>
              </div>
            </motion.div>
          </motion.div>

          {/* Text column */}
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 1,
          delay: 0.2,
          ease: easeOutCubic
        }} className="flex flex-col justify-center" data-id="element-116">
            <div className="inline-flex items-center gap-3 mb-5" data-id="element-117">
              <span className="w-8 h-px bg-brand-gold" data-id="element-118"></span>
              <span className="text-brand-dark/50 font-medium tracking-[0.25em] text-[10px] uppercase" data-id="element-119">
                {t('doctor.eyebrow')}
              </span>
            </div>

            <h2 className="text-3xl md:text-4xl lg:text-[2.75rem] font-serif font-light text-brand-dark mb-5 tracking-tight leading-[1.1]" data-id="element-120">
              {t('doctor.heading')}
            </h2>

            <p className="text-base lg:text-lg text-brand-dark/70 font-light leading-relaxed mb-8" data-id="element-121">
              {t('doctor.subheading')}
            </p>

            <div className="space-y-6 pt-6 border-t border-brand-stone/50" data-id="element-122">
              {pillars.map((pillar, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.7,
              delay: 0.4 + idx * 0.12,
              ease: easeOutCubic
            }} className="flex gap-4" data-id="element-123">
                  <span className="font-serif text-lg text-brand-gold/70 font-light italic shrink-0 mt-1" data-id="element-124">
                    0{idx + 1}
                  </span>
                  <div data-id="element-125">
                    <h3 className="font-serif text-lg lg:text-xl text-brand-dark font-normal mb-1.5" data-id="element-126">
                      {pillar.title}
                    </h3>
                    <p className="text-brand-dark/65 font-light leading-relaxed text-sm" data-id="element-127">
                      {pillar.body}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}